import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import CreatableSelect from 'react-select/creatable';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { CURRENCY_SYMBOLS } from '../utils/currencySymbols';
import { Tooltip } from 'react-tooltip';
import { FaInfoCircle } from 'react-icons/fa';
import { FiLoader } from 'react-icons/fi';

const RecurringModal = ({ isOpen, onClose, expense, onSuccess, categories = [], updateCategories, currencies }) => {
  const [isLoading, setIsLoading] = useState(false);

  const calculateNextPaymentDate = (date, periodNumber, periodUnit) => {
    if (!date || !periodNumber || !periodUnit || isNaN(periodNumber) || periodNumber <= 0) {
      return null;
    }

    const nextDate = new Date(date);
    const number = parseInt(periodNumber);
    
    switch (periodUnit.toLowerCase()) {
      case 'day':
        nextDate.setDate(nextDate.getDate() + number);
        break;
      case 'week':
        nextDate.setDate(nextDate.getDate() + (number * 7));
        break;
      case 'month':
        nextDate.setMonth(nextDate.getMonth() + number);
        break;
      case 'year':
        nextDate.setFullYear(nextDate.getFullYear() + number);
        break;
      default:
        return null;
    }
    
    return nextDate;
  };

  const getMinEndDate = () => {
    // For one-off payments, return start date
    if (formData.isOneOff || !formData.period) {
      return formData.startDate;
    }

    if (!formData.startDate || !formData.period?.number || isNaN(formData.period?.number) || formData.period?.number <= 0) {
      return '';
    }

    // Get today's date
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // For paused expenses being resumed, always calculate from the new start date
    if (expense?.isPaused) {
      const startDate = new Date(formData.startDate);
      const endDate = calculateNextPaymentDate(
        formData.startDate,
        formData.period.number,
        formData.period.unit
      );
      return endDate.toISOString().split('T')[0];
    }

    // If editing an existing expense, use the original end date if it's in the future
    if (expense?.endDate && !expense?.isPaused) {
      const originalEndDate = new Date(expense.endDate);
      originalEndDate.setHours(0, 0, 0, 0);
      
      if (originalEndDate > today) {
        return expense.endDate.split('T')[0];
      }
    }

    // Calculate the end date as start date + one period
    const startDate = new Date(formData.startDate);
    const endDate = calculateNextPaymentDate(
      formData.startDate,
      formData.period.number,
      formData.period.unit
    );

    // If the calculated end date is in the past, use next future payment date
    if (endDate <= today) {
      const futurePayment = calculateNextPaymentDate(
        today.toISOString(),
        formData.period.number,
        formData.period.unit
      );
      return futurePayment.toISOString().split('T')[0];
    }

    return endDate.toISOString().split('T')[0];
  };

  const getTodayDate = () => {
    const today = new Date();
    
    // Get year, month, and day in local timezone
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(today.getDate()).padStart(2, '0');
    
    // Format as YYYY-MM-DD
    return `${year}-${month}-${day}`;
  };

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    isOneOff: false,
    title: '',
    amount: '',
    currency: currencies?.[0] || 'USD',
    startDate: '',
    category: null,
    period: null,
    hasEndDate: false,
    endDate: ''
  });

  // Add helper to get appropriate start date for paused expenses
  const getResumeStartDate = (expense) => {
    if (!expense || !expense.isPaused) return getTodayDate();
    
    const nextPayment = new Date(expense.nextPaymentDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // If next payment is today or in the past, use tomorrow
    if (nextPayment <= today) {
      return getTodayDate();
    }
    
    // Otherwise use the stored next payment date
    return nextPayment.toISOString().split('T')[0];
  };

  // Update useEffect to handle paused expenses correctly
  useEffect(() => {
    if (expense) {
      setFormData({
        isOneOff: expense.isOneOff || false,
        title: expense.title,
        amount: expense.amount ? Number(expense.amount).toFixed(2) : '',
        currency: expense.currency || currencies?.[0] || 'USD',
        startDate: expense.isPaused ? getResumeStartDate(expense) : new Date(expense.startDate).toISOString().split('T')[0],
        category: expense.category ? { value: expense.category, label: expense.category } : null,
        period: expense.isOneOff ? null : (expense.period ? { 
          number: expense.period.number && expense.period.number !== '' ? expense.period.number : '1',
          unit: expense.period.unit || 'month'
        } : { number: '1', unit: 'month' }),
        hasEndDate: !!expense.endDate,
        endDate: expense.endDate || ''
      });
    } else {
      setFormData({
        isOneOff: false,
        title: '',
        amount: '',
        currency: currencies?.[0] || 'USD',
        startDate: getTodayDate(),
        category: null,
        period: { number: '1', unit: 'month' },
        hasEndDate: false,
        endDate: ''
      });
    }
  }, [expense, currencies]);

  // Update the period change effect to handle both unit and number changes
  useEffect(() => {
    if (formData.hasEndDate && formData.startDate && formData.period) {
      const minEndDate = getMinEndDate();
      // Always update end date for new expenses and when resuming paused expenses
      if (!expense || (expense && expense.isPaused)) {
        setFormData(prev => ({
          ...prev,
          endDate: minEndDate
        }));
      }
    }
  }, [formData.period?.number, formData.period?.unit, formData.startDate, formData.hasEndDate, expense]);

  // Add effect to handle one-off payment changes
  useEffect(() => {
    if (formData.isOneOff) {
      setFormData(prev => ({
        ...prev,
        hasEndDate: true,
        endDate: prev.startDate,
        period: null
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        period: prev.period || { number: '1', unit: 'month' }
      }));
    }
  }, [formData.isOneOff, formData.startDate]);

  // Add this helper function
  const isStartDateInPast = () => {
    if (!formData.startDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(formData.startDate);
    start.setHours(0, 0, 0, 0);
    return start < today;
  };

  // Update the start date change handler
  const handleStartDateChange = (e) => {
    const newStartDate = e.target.value;
    setFormData(prev => ({
      ...prev,
      startDate: newStartDate,
      // For one-off payments, sync all dates
      ...(prev.isOneOff && {
        endDate: newStartDate
      })
    }));
  };

  if (!isOpen) return null;

  const categoryOptions = categories
    .map(cat => ({ value: cat, label: cat }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const currencyOptions = currencies?.map(curr => ({ 
    value: curr, 
    label: `${curr} (${CURRENCY_SYMBOLS[curr]})`
  })) || [];

  const handleCreateCategory = async (inputValue) => {
    const newCategory = inputValue.trim();
    if (newCategory && !categories.includes(newCategory)) {
      try {
        const newCategories = [...categories, newCategory];
        await updateCategories(newCategories);
        
        setFormData(prev => ({
          ...prev,
          category: { value: newCategory, label: newCategory }
        }));
        
        toast.success('Category created successfully');
      } catch (error) {
        toast.error('Failed to create category');
      }
    }
  };

  const handleAmountChange = (e) => {
    let value = e.target.value;
    
    // Remove any non-numeric characters except decimal point
    value = value.replace(/[^\d.]/g, '');
    
    // Ensure only one decimal point
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }

    setFormData(prev => ({ ...prev, amount: value }));
  };

  // Update handlePeriodNumberChange
  const handlePeriodNumberChange = (e) => {
    const value = e.target.value;
    // Only restrict changes for active recurring expenses (not one-off)
    if (isStartDateInPast() && expense && !expense.isPaused && !expense.isOneOff) return;
    
    // Don't allow empty values - always ensure we have a valid number
    if (value === '') {
      // If user deletes the value, reset to 1
      setFormData(prev => ({
        ...prev,
        period: prev.period ? { ...prev.period, number: '1' } : { number: '1', unit: 'month' }
      }));
    } else if (Number(value) > 0 && !isNaN(value)) {
      setFormData(prev => ({
        ...prev,
        period: prev.period ? { ...prev.period, number: value } : { number: value, unit: 'month' }
      }));
    }
  };

  // Update handlePeriodUnitChange
  const handlePeriodUnitChange = (e) => {
    // Only restrict changes for active recurring expenses (not one-off)
    if (isStartDateInPast() && expense && !expense.isPaused && !expense.isOneOff) return;
    
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      period: prev.period ? { ...prev.period, unit: value } : { number: '1', unit: value }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    
    setIsLoading(true);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    try {
      // Validate period data for non-one-off payments
      if (!formData.isOneOff) {
        if (!formData.period?.number || formData.period.number === '' || isNaN(formData.period.number) || Number(formData.period.number) <= 0) {
          toast.error('Please enter a valid period number');
          setIsLoading(false);
          return;
        }
        if (!formData.period?.unit) {
          toast.error('Please select a valid period unit');
          setIsLoading(false);
          return;
        }
      }

      // If editing an existing recurring expense
      if (expense) {
        let nextPaymentDate;

        if (formData.isOneOff) {
          // For one-off payments, all dates should be the same
          nextPaymentDate = new Date(formData.startDate);
        } else if (expense.isPaused) {
          nextPaymentDate = new Date(formData.startDate);
        } else {
          // For active expenses, calculate next payment based on current nextPaymentDate
          nextPaymentDate = new Date(expense.nextPaymentDate);
          nextPaymentDate.setHours(0, 0, 0, 0);

          // If the next payment is today or in the past, calculate the next future payment
          if (nextPaymentDate <= today && !formData.isOneOff) {
            const normalizedUnit = formData.period?.unit.toLowerCase().replace(/s$/, '');
            switch (normalizedUnit) {
              case 'day':
                nextPaymentDate.setDate(nextPaymentDate.getDate() + parseInt(formData.period.number));
                break;
              case 'week':
                nextPaymentDate.setDate(nextPaymentDate.getDate() + (parseInt(formData.period.number) * 7));
                break;
              case 'month':
                nextPaymentDate.setMonth(nextPaymentDate.getMonth() + parseInt(formData.period.number));
                break;
              case 'year':
                nextPaymentDate.setFullYear(nextPaymentDate.getFullYear() + parseInt(formData.period.number));
                break;
            }
          }
        }

        const data = {
          isOneOff: formData.isOneOff,
          title: formData.title.trim(),
          amount: formData.amount,
          currency: formData.currency,
          category: formData.category?.value || '',
          period: formData.isOneOff ? null : {
            number: formData.period.number || '1',
            unit: formData.period.unit || 'month'
          },
          nextPaymentDate: nextPaymentDate.toISOString(),
          userId: currentUser.uid,
          isPaused: false,
          startDate: formData.startDate,
          endDate: formData.isOneOff ? formData.startDate : (formData.hasEndDate ? formData.endDate : null),
        };

        await updateDoc(doc(db, 'recurringExpenses', expense.id), data);
        toast.success('Recurring expense updated successfully');
        onSuccess();
      } else {
        // For new recurring expenses
        const startDate = new Date(formData.startDate);
        const data = {
          isOneOff: formData.isOneOff,
          title: formData.title.trim(),
          amount: formData.amount,
          currency: formData.currency,
          startDate: startDate.toISOString(),
          category: formData.category?.value || '',
          period: formData.isOneOff ? null : {
            number: formData.period.number || '1',
            unit: formData.period.unit || 'month'
          },
          nextPaymentDate: startDate.toISOString(),
          userId: currentUser.uid,
          isPaused: false,
          endDate: formData.isOneOff ? formData.startDate : (formData.hasEndDate ? formData.endDate : null),
        };

        await addDoc(collection(db, 'recurringExpenses'), data);
        toast.success('Recurring expense created successfully');
        onSuccess();
      }
    } catch (error) {
      console.error('Error saving recurring expense:', error);
      toast.error('Failed to save recurring expense');
    } finally {
      setIsLoading(false);
    }
  };

  const customStyles = {
    input: (base) => ({
      ...base,
      'input:focus': {
        boxShadow: 'none',
      },
    }),
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? '#6366f1' : '#e5e7eb',
      boxShadow: state.isFocused ? '0 0 0 1px #6366f1' : 'none',
      '&:hover': {
        borderColor: '#6366f1',
      },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? '#6366f1' 
        : state.isFocused 
          ? '#e0e7ff' 
          : 'white',
      color: state.isSelected ? 'white' : '#374151',
      '&:active': {
        backgroundColor: '#6366f1',
      },
    }),
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-xl h-2/3 2xl:h-max overflow-y-auto shadow-xl">
        <h3 className="text-xl font-semibold mb-6 text-gray-800 dark:text-gray-200">
          {expense?.isPaused ? 'Resume' : expense ? 'Edit' : 'Add'} Recurring Payments
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              One-off Scheduled Payment
            </label>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={formData.isOneOff}
                onChange={(e) => {
                  if (expense && !expense.isPaused) return;
                  setFormData(prev => ({
                    ...prev,
                    isOneOff: e.target.checked
                  }));
                }}
                disabled={expense && !expense.isPaused}
              />
              <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 
                peer-focus:ring-purple-300 dark:peer-focus:ring-purple-800 rounded-full peer 
                dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white 
                after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white 
                after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 
                after:transition-all dark:border-gray-600 peer-checked:bg-purple-600
                ${expense && !expense.isPaused ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Title
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 
                focus:ring-2 focus:ring-purple-500 focus:border-purple-500 
                transition-colors duration-200
                dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Category
            </label>
            <CreatableSelect
              options={categoryOptions}
              value={formData.category}
              onChange={(selected) => setFormData(prev => ({ ...prev, category: selected }))}
              onCreateOption={handleCreateCategory}
              styles={customStyles}
              placeholder="Select or create a category"
              className="react-select-container"
              classNamePrefix="react-select"
              formatCreateLabel={(inputValue) => `Create category "${inputValue}"`}
              required
            />
          </div>

          <div className="grid grid-cols-3 gap-4 items-center">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Currency
              </label>
              <Select
                options={currencyOptions}
                value={currencyOptions.find(opt => opt.value === formData.currency)}
                onChange={(selected) => setFormData(prev => ({ 
                  ...prev, 
                  currency: selected.value 
                }))}
                styles={customStyles}
                isSearchable={false}
              />
            </div>
            
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Amount (each payment)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-gray-500 dark:text-gray-400">
                  {CURRENCY_SYMBOLS[formData.currency]}
                </span>
                <input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={handleAmountChange}
                  onBlur={(e) => {
                    if (e.target.value && !isNaN(e.target.value)) {
                      setFormData(prev => ({
                        ...prev,
                        amount: Number(e.target.value).toFixed(2)
                      }));
                    }
                  }}
                  className="w-full pl-8 pr-4 py-2.5 rounded-lg border border-gray-300 
                    focus:ring-2 focus:ring-purple-500 focus:border-purple-500 
                    transition-colors duration-200
                    dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                  required
                />
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Start Date
              </label>
              <FaInfoCircle 
                className="text-gray-400 hover:text-gray-600 cursor-help w-4 h-4 mb-1" 
                data-tooltip-id="start-date-tooltip"
              />
              <Tooltip
                id="start-date-tooltip"
                place="right"
                content="Past dates require manual entries. Recurring only works for future dates."
                className="z-50 dark:bg-gray-100 dark:text-gray-700"
              />
            </div>
            <input
              type="date"
              value={formData.startDate}
              min={(!expense || expense.isPaused) ? getTodayDate() : undefined}
              onChange={handleStartDateChange}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 
                focus:ring-2 focus:ring-purple-500 focus:border-purple-500 
                transition-colors duration-200
                dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              required
              disabled={expense && !expense.isPaused && !expense.isOneOff}
            />
          </div>

          {/* Only show period fields if not one-off */}
          {!formData.isOneOff && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Repeat Every
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.period?.number || ''}
                  onChange={handlePeriodNumberChange}
                  placeholder="1"
                  className={`w-full px-4 py-2.5 rounded-lg border border-gray-300 
                    focus:ring-2 focus:ring-purple-500 focus:border-purple-500 
                    transition-colors duration-200
                    dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200
                    ${expense && !expense.isPaused ? 'opacity-50 cursor-not-allowed' : ''}`}
                  required={!formData.isOneOff}
                  disabled={expense && !expense.isPaused}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Period
                </label>
                <select
                  value={formData.period?.unit || 'month'}
                  onChange={handlePeriodUnitChange}
                  className={`w-full px-4 py-2.5 rounded-lg border border-gray-300 
                    focus:ring-2 focus:ring-purple-500 focus:border-purple-500 
                    transition-colors duration-200
                    dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200
                    ${expense && !expense.isPaused ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={expense && !expense.isPaused}
                  required={!formData.isOneOff}
                >
                  <option value="day">Day</option>
                  <option value="week">Week</option>
                  <option value="month">Month</option>
                  <option value="year">Year</option>
                </select>
              </div>
            </div>
          )}

          {expense && !expense.isPaused && !expense.isOneOff && (
            <p className="text-xs text-amber-600 mt-1">
              Period and end date cannot be changed for existing recurring expenses
            </p>
          )}

          {!formData.isOneOff && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Add End Date
                </label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={formData.hasEndDate}
                    onChange={(e) => {
                      if (expense && !expense.isPaused) return;
                      setFormData(prev => ({
                        ...prev,
                        hasEndDate: e.target.checked,
                        endDate: e.target.checked ? getMinEndDate() : ''
                      }))
                    }}
                    disabled={expense && !expense.isPaused}
                  />
                  <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 
                    peer-focus:ring-purple-300 dark:peer-focus:ring-purple-800 rounded-full peer 
                    dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white 
                    after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white 
                    after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 
                    after:transition-all dark:border-gray-600 peer-checked:bg-purple-600
                    ${expense && !expense.isPaused ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
                </label>
              </div>

              {formData.hasEndDate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    min={getMinEndDate()}
                    onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                    className={`w-full px-4 py-2.5 rounded-lg border border-gray-300 
                      focus:ring-2 focus:ring-purple-500 focus:border-purple-500 
                      transition-colors duration-200
                      dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200
                      ${expense && !expense.isPaused ? 'opacity-50 cursor-not-allowed' : ''}`}
                    required={formData.hasEndDate}
                    disabled={expense && !expense.isPaused}
                  />
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 
                hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700 
                dark:border-gray-600 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={`px-4 py-2 rounded-lg text-white transition-colors duration-200 
                ${formData.isOneOff 
                  ? 'bg-orange-600/80 hover:bg-orange-700/80'
                  : formData.hasEndDate 
                    ? 'bg-blue-600/80 hover:bg-blue-700/80'
                    : 'bg-purple-600/80 hover:bg-purple-700/80'
                } disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
            >
              {isLoading ? (
                <>
                  <FiLoader className="animate-spin" />
                  {expense?.isPaused ? 'Resuming...' : expense ? 'Updating...' : 'Adding...'}
                </>
              ) : (
                <>
                  {expense?.isPaused ? 'Resume' : expense ? 'Update' : 'Add'}
                  &nbsp;
                  {formData.isOneOff 
                    ? 'Scheduled one time payment'
                    : formData.hasEndDate 
                      ? 'Fixed term payment/installment'
                      : 'Subscription'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecurringModal;
